// src/pages/negative/AdminNegativeTable.jsx
import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Badge,
  Button,
  Card,
  Table,
  TextInput,
  Select,
  Pagination,
  Modal,
  Label,
  Alert,
  Spinner,
} from "flowbite-react";
import {
  HiOutlineSearch,
  HiCheckCircle,
  HiShieldCheck,
  HiOutlineEye,
} from "react-icons/hi";
import { MdErrorOutline } from "react-icons/md";
import { MdFileDownload } from "react-icons/md";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { FaMoneyBillWave } from "react-icons/fa";
import { RxUpdate } from "react-icons/rx";
import { LuFilter, LuFilterX } from "react-icons/lu";
import { BsFillBarChartFill } from "react-icons/bs";
import * as XLSX from "xlsx";
import axios from "../../config/axiosInstance";
import { ADMIN, NEGATIVE } from "../../config/apiConfig";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { formatDocument } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";
import { Tooltip } from "react-tooltip";
import NegativePromoHero from "../common/NegativePromoHero";
import AutoCompleteInput from "../inputs/Autocomplete";

const MOTIVOS_BAIXA = [
  {
    Codigo: "01",
    Motivo: "Pagamento da dívida",
    Descricao: "O devedor pagou a dívida.",
  },
  {
    Codigo: "03",
    Motivo: "Determinação Judicial",
    Descricao: "Baixa da anotação para cumprimento de determinação judicial.",
  },
  {
    Codigo: "05",
    Motivo: "Ordem Depto Jurídico",
    Descricao: "Ordem Depto Jurídico",
  },
  {
    Codigo: "06",
    Motivo: "Documento Roubado/BO",
    Descricao: "Documento Roubado",
  },
  {
    Codigo: "09",
    Motivo: "Falecimento do Devedor",
    Descricao: "Falecimento do Devedor",
  },
  { Codigo: "26", Motivo: "Pagamento Inibido", Descricao: "Pagamento Inibido" },
];

// helpers
const brl = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const STATUS_LABELS = {
  ABERTO: "Aguardando aprovação",
  ENVIADA: "Em andamento",
  ACEITA: "Sucesso",
  ACEITO: "Sucesso",
  PENDENTE: "Alterações pendentes",
  REJEITADA: "Rejeitada",
  REJEITADO: "Rejeitada",
  BAIXA_SOLICITADA: "Baixa solicitada",
  BAIXADA: "Baixa realizada",
  BAIXADO: "Baixa realizada",
};

const StatusBadge = ({ status }) => {
  const S = String(status || "").toUpperCase();
  const map = {
    ABERTO: "warning",
    ENVIADA: "info",
    PENDENTE: "warning",
    BAIXA_SOLICITADA: "info",
    ACEITA: "success",
    REJEITADA: "failure",
    BAIXADA: "gray",
    BAIXADO: "gray",
    ACEITO: "success",
    REJEITADO: "failure",
  };
  const label = STATUS_LABELS[S] || S || "—";
  return (
    <Badge color={map[S] || "indigo"} className="w-fit mx-auto">
      {label}
    </Badge>
  );
};

function useNegatives({
  companyId,
  representativeId,
  customerId,
  q,
  status,
  dateFrom,
  dateTo,
  page,
  pageSize,
  orderBy,
  orderDir,
  isActive,
}) {
  return useQuery(
    [
      "negatives",
      {
        companyId,
        representativeId,
        customerId,
        q,
        status,
        dateFrom,
        dateTo,
        page,
        pageSize,
        orderBy,
        orderDir,
      },
    ],
    async () => {
      const params = {
        // Mantém compat com o que você já usa
        companyId,
        // Novos filtros (enviados só quando selecionados)
        representativeId: representativeId || undefined,
        customerId: customerId || undefined,
        q: q || undefined,
        status: status || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        pageSize,
        orderBy,
        orderDir,
      };
      const { data } = await axios.get(NEGATIVE.LIST, { params });
      return data; // { items, total, page, pageSize, totalPages }
    },
    { keepPreviousData: true, enabled: isActive },
  );
}

function useNegativeDetail(id, open) {
  return useQuery(
    ["negative-detail", id],
    async () => {
      const { data } = await axios.get(`${NEGATIVE.GET(id)}?all=1`);
      return data;
    },
    { enabled: !!id && open },
  );
}

export default function AdminNegativeTable({ isActive }) {
  const { user } = useApplication();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Filtros básicos
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");

  // Filtros novos: Representante e Cliente
  const [selectedRepresentative, setSelectedRepresentative] = React.useState({
    label: "Todos os representantes",
    value: "",
  });
  const [selectedCustomer, setSelectedCustomer] = React.useState({
    label: "Todos os clientes",
    value: 0,
  });
  const [appliedRepresentative, setAppliedRepresentative] = React.useState({
    label: "Todos os representantes",
    value: "",
  });
  const [appliedCustomer, setAppliedCustomer] = React.useState({
    label: "Todos os clientes",
    value: 0,
  });
  const [appliedQ, setAppliedQ] = React.useState("");
  const [appliedStatus, setAppliedStatus] = React.useState("");
  const [appliedDateFrom, setAppliedDateFrom] = React.useState("");
  const [appliedDateTo, setAppliedDateTo] = React.useState("");

  // Paginação/ordenação
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(20);
  const [orderBy] = React.useState("id");
  const [orderDir] = React.useState("DESC");
  const [currentPage, setCurrentPage] = React.useState(1);

  // UI Filtros
  const [showFilters, setShowFilters] = React.useState(true);
  const [renderFilters, setRenderFilters] = React.useState(false);
  const [filtersDirty, setFiltersDirty] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  React.useEffect(() => {
    if (showFilters) setRenderFilters(true);
    else {
      const t = setTimeout(() => setRenderFilters(false), 400);
      return () => clearTimeout(t);
    }
  }, [showFilters]);

  const syncToPage = (p) => {
    setCurrentPage(p);
    setPage(p);
  };

  const markFiltersDirty = () => setFiltersDirty(true);
  const applyFilters = () => {
    setAppliedRepresentative(selectedRepresentative);
    setAppliedCustomer(selectedCustomer);
    setAppliedQ(q);
    setAppliedStatus(status);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    syncToPage(1);
    setFiltersDirty(false);
  };

  // Lists: Representatives
  const {
    isLoading: isLoadingRepresentatives,
    error: errorListRepresentatives,
    data: listRepresentatives,
  } = useQuery(
    ["listRepresentatives"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`,
      );
      const reps = [
        { label: "Todos os representantes", value: "" },
        {
          label: user?.Company?.name || user?.Company?.socialReason,
          value: user?.companyId,
        },
        ...data?.data?.data?.map((representative) => ({
          value: representative.id,
          label: representative?.name || representative?.socialReason,
        })),
      ];
      return reps;
    },
    { enabled: !!user && isActive },
  );

  // Lists: Customers (dependente do representative)
  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers", selectedRepresentative?.value],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1&representativeId=${
          selectedRepresentative?.value || ""
        }`,
      );
      let customers =
        data?.data?.data?.map((customer) => ({
          label:
            customer.typeCustomer === "CPF"
              ? customer.name
              : customer.socialReason,
          value: customer.id,
        })) || [];
      customers = [{ label: "Todos os clientes", value: 0 }, ...customers];
      return customers;
    },
    { enabled: !!user && isActive },
  );

  // Busca principal
  const { data, isLoading, isFetching } = useNegatives({
    companyId: user?.Company?.id,
    representativeId: appliedRepresentative?.value || undefined,
    customerId: appliedCustomer?.value ? appliedCustomer.value : undefined,
    q: appliedQ,
    status: appliedStatus,
    dateFrom: appliedDateFrom,
    dateTo: appliedDateTo,
    page,
    pageSize,
    orderBy,
    orderDir,
    isActive,
  });

  // loading por linha ao consultar status
  const [statusLoadingIds, setStatusLoadingIds] = React.useState(new Set());
  const isRowLoading = (id) => statusLoadingIds.has(id);

  // mutations
  const sendExternal = useMutation(
    async (id) => {
      const { data } = await axios.post(NEGATIVE.SEND(id), {});
      return data;
    },
    { onSuccess: () => qc.invalidateQueries("negatives") },
  );

  const baixa = useMutation(
    async ({ id, motivoBaixa }) => {
      const { data } = await axios.post(NEGATIVE.BAIXA(id), { motivoBaixa });
      return data;
    },
    {
      onSuccess: (_, { id }) => {
        qc.invalidateQueries("negatives");
        qc.invalidateQueries(["negative-detail", id]);
      },
    },
  );

  const searchStatus = async (negativeId) => {
    try {
      setStatusLoadingIds((prev) => {
        const next = new Set(prev);
        next.add(negativeId);
        return next;
      });
      await axios.get(`${NEGATIVE.SEARCH_STATUS}/${negativeId}`);
      qc.invalidateQueries("negatives");
      qc.invalidateQueries(["negative-detail", negativeId]);
    } catch (error) {
      // opcional: toast/alert
    } finally {
      setStatusLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(negativeId);
        return next;
      });
    }
  };

  // modal baixa
  const [openBaixa, setOpenBaixa] = React.useState(false);
  const [baixaId, setBaixaId] = React.useState(null);
  const [motivoCodigo, setMotivoCodigo] = React.useState("");

  const openBaixaModal = (id) => {
    setBaixaId(id);
    setMotivoCodigo("");
    setOpenBaixa(true);
  };

  // modal detalhes
  const [openDetail, setOpenDetail] = React.useState(false);
  const [detailId, setDetailId] = React.useState(null);
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useNegativeDetail(detailId, openDetail);

  const totalPages = data?.totalPages || 1;
  const items = data?.items || [];
  const total = data?.total || 0;

  const hasActiveFilters = React.useMemo(
    () =>
      Boolean(
        appliedRepresentative?.value ||
        appliedCustomer?.value ||
        appliedQ ||
        appliedStatus ||
        appliedDateFrom ||
        appliedDateTo,
      ),
    [
      appliedRepresentative?.value,
      appliedCustomer?.value,
      appliedQ,
      appliedStatus,
      appliedDateFrom,
      appliedDateTo,
    ],
  );

  const appliedFilterSummary = React.useMemo(() => {
    const list = [];
    if (appliedRepresentative?.value) {
      list.push(`Representante: ${appliedRepresentative?.label}`);
    }
    if (appliedCustomer?.value) {
      list.push(`Cliente: ${appliedCustomer?.label}`);
    }
    if (appliedStatus) {
      list.push(
        `Status: ${STATUS_LABELS[String(appliedStatus).toUpperCase()] || appliedStatus}`,
      );
    }
    if (appliedQ) {
      list.push(`Busca: ${appliedQ}`);
    }
    if (appliedDateFrom || appliedDateTo) {
      list.push(
        `Período: ${appliedDateFrom || "..."} até ${appliedDateTo || "..."}`,
      );
    }
    return list;
  }, [
    appliedRepresentative?.value,
    appliedRepresentative?.label,
    appliedCustomer?.value,
    appliedCustomer?.label,
    appliedStatus,
    appliedQ,
    appliedDateFrom,
    appliedDateTo,
  ]);

  // KPIs (cards-resumo)
  const kpi = React.useMemo(() => {
    const acc = {
      totalAberto: 0,
      totalAceito: 0,
      totalRejeitado: 0,
      totalBaixado: 0,
    };
    for (const it of items) {
      const s = String(it?.status || "").toUpperCase();
      if (["ABERTO", "ENVIADA", "PENDENTE", "BAIXA_SOLICITADA"].includes(s))
        acc.totalAberto += 1;
      else if (["ACEITA", "ACEITO"].includes(s)) acc.totalAceito += 1;
      else if (["REJEITADA", "REJEITADO"].includes(s)) acc.totalRejeitado += 1;
      else if (["BAIXADA", "BAIXADO"].includes(s)) acc.totalBaixado += 1;
    }
    return acc;
  }, [items]);

  const formatDateSafe = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "dd/MM/yyyy");
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);

      const params = {
        companyId: user?.Company?.id,
        representativeId: appliedRepresentative?.value || undefined,
        customerId: appliedCustomer?.value ? appliedCustomer.value : undefined,
        q: appliedQ || undefined,
        status: appliedStatus || undefined,
        dateFrom: appliedDateFrom || undefined,
        dateTo: appliedDateTo || undefined,
        orderBy,
        orderDir,
      };

      const { data: response } = await axios.get(NEGATIVE.LIST, { params });
      const exportItems = response?.items || [];

      if (!exportItems.length) {
        alert("Nenhum registro encontrado para exportar.");
        return;
      }

      const rows = exportItems.map((it) => {
        const normalizedStatus = String(it?.status || "").toUpperCase();
        return {
          id: it?.id || "",
          devedor: it?.nome || "",
          documento: formatDocument(it?.documentoDevedor) || "",
          cliente: it?.Company?.name || it?.Company?.socialReason || "",
          usuario: it?.User?.name || "",
          dataInclusao: formatDateSafe(it?.createdAt),
          dataDisponivel: formatDateSafe(
            it?.dataDisponivel || it?.dataDisponibilidade,
          ),
          status: STATUS_LABELS[normalizedStatus] || normalizedStatus || "",
          valor: Number(it?.valor || 0),
          contrato: it?.contrato || "",
        };
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rows, {
        header: [
          "id",
          "devedor",
          "documento",
          "cliente",
          "usuario",
          "dataInclusao",
          "dataDisponivel",
          "status",
          "valor",
          "contrato",
        ],
      });

      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 34 },
        { wch: 18 },
        { wch: 28 },
        { wch: 24 },
        { wch: 14 },
        { wch: 14 },
        { wch: 22 },
        { wch: 12 },
        { wch: 18 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "Negativacoes");

      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const filename = `negativacoes_${now.getFullYear()}-${pad(
        now.getMonth() + 1,
      )}-${pad(now.getDate())}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error(error);
      alert("Não foi possível exportar o Excel. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="p-4">
        {/* Toggle filtros */}
        <div className="flex justify-between items-center mb-2">
          <Button
            size="xs"
            onClick={() => setShowFilters((prev) => !prev)}
            className="bg-white text-slate-700 border border-slate-200 shadow-sm hover:bg-gray-50 hover:text-white transition-all duration-300"
          >
            <div className="flex items-center gap-2 transition-all duration-300">
              <span>
                {showFilters ? "Esconder filtros" : "Mostrar filtros"}
              </span>
              <span className="transition-transform duration-300">
                {!showFilters ? <LuFilter /> : <LuFilterX />}
              </span>
            </div>
          </Button>
        </div>

        {/* Filtros avançados */}
        {renderFilters && (
          <div
            className={[
              "relative bg-white border border-slate-200 rounded-xl shadow-sm transition-all duration-500 ease-in-out overflow-hidden",
              showFilters ? "max-h-[1200px]" : "max-h-0",
            ].join(" ")}
          >
            {(isLoading || isFetching) && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
                <Spinner />
              </div>
            )}

            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50 overflow-hidden">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Filtros Avançados
                </span>
                {filtersDirty && (
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    Alterações pendentes
                  </span>
                )}
              </div>
              <Button
                size="xs"
                color="success"
                onClick={applyFilters}
                isProcessing={isLoading || isFetching}
              >
                Aplicar filtros
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 px-3 py-3">
              {/* Representante */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">
                  Representante
                </label>
                <div className="h-9">
                  <AutoCompleteInput
                    options={listRepresentatives}
                    label=""
                    onChange={(rep) => {
                      setSelectedRepresentative(rep);
                      markFiltersDirty();
                      setSelectedCustomer({
                        label: "Todos os clientes",
                        value: 0,
                      });
                    }}
                    value={selectedRepresentative?.value}
                    isLoading={isLoadingRepresentatives}
                    className="h-full"
                  />
                </div>
                {errorListRepresentatives && (
                  <Alert color="failure" className="mt-2">
                    Ocorreu um erro ao buscar representantes
                  </Alert>
                )}
              </div>

              {/* Cliente */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">
                  Cliente
                </label>
                <div className="h-9">
                  <AutoCompleteInput
                    options={listCustomers}
                    label=""
                    onChange={(c) => {
                      setSelectedCustomer(c);
                      markFiltersDirty();
                    }}
                    value={selectedCustomer?.value}
                    isLoading={isLoadingCustomers}
                    className="h-full"
                  />
                </div>
                {errorListCustomers && (
                  <Alert color="failure" className="mt-2">
                    Ocorreu um erro ao buscar clientes
                  </Alert>
                )}
              </div>

              {/* Busca livre */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">
                  Busca
                </label>
                <TextInput
                  icon={HiOutlineSearch}
                  placeholder="Nome, documento, contrato..."
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    markFiltersDirty();
                  }}
                />
              </div>

              {/* Status */}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">
                  Status
                </label>
                <Select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    markFiltersDirty();
                  }}
                >
                  <option value="">Todos</option>
                  <option value="ABERTO">Aguardando aprovação</option>
                  <option value="ENVIADA">Em andamento</option>
                  <option value="ACEITO">Sucesso</option>
                  <option value="PENDENTE">Alterações pendentes</option>
                  <option value="REJEITADO">Rejeitada</option>
                  <option value="BAIXA_SOLICITADA">Baixa solicitada</option>
                  <option value="BAIXADO">Baixa realizada</option>
                </Select>
              </div>

              {/* Período */}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">
                  Venc. de
                </label>
                <TextInput
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    markFiltersDirty();
                  }}
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">
                  Venc. até
                </label>
                <TextInput
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    markFiltersDirty();
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* KPIs (cards) */}
        <div className="overflow-x-auto">
          <div className="flex gap-4 pb-3 min-w-[800px] mt-4">
            <div className="min-w-[220px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-amber-600 to-yellow-500 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Abertas/Pendentes</p>
                  <h2 className="text-2xl font-bold mt-1">{kpi.totalAberto}</h2>
                </div>
                <MdErrorOutline className="text-white text-4xl opacity-20" />
              </div>
            </div>

            <div className="min-w-[220px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-emerald-600 to-teal-500 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Negativadas</p>
                  <h2 className="text-2xl font-bold mt-1">{kpi.totalAceito}</h2>
                </div>
                <IoIosCheckmarkCircleOutline className="text-white text-4xl opacity-20" />
              </div>
            </div>

            <div className="min-w-[220px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-rose-600 to-pink-500 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Rejeitadas</p>
                  <h2 className="text-2xl font-bold mt-1">
                    {kpi.totalRejeitado}
                  </h2>
                </div>
                <MdErrorOutline className="text-white text-4xl opacity-20" />
              </div>
            </div>

            <div className="min-w-[220px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-slate-600 to-slate-500 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Baixadas</p>
                  <h2 className="text-2xl font-bold mt-1">
                    {kpi.totalBaixado}
                  </h2>
                </div>
                <FaMoneyBillWave className="text-white text-4xl opacity-20" />
              </div>
            </div>
          </div>
        </div>

        {/* Barra de ações */}
        <div className="mt-3">
          <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-4 py-2 shadow-sm md:flex-row md:items-center md:justify-between mb-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                  <BsFillBarChartFill className="text-xl" />
                </div>
                <p className="text-base font-semibold text-gray-900">
                  Resultado da busca
                </p>
              </div>
              {filtersDirty && (
                <div className="text-xs italic text-gray-500">
                  Alterações ainda não aplicadas (clique em Aplicar filtros)
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 justify-end text-xs">
              <Button
                size="xs"
                color="success"
                onClick={handleExportExcel}
                isProcessing={isExporting}
                disabled={isExporting || isLoading || isFetching}
                className="flex items-center gap-2"
              >
                <MdFileDownload className="text-base" />
                <span>Exportar Excel</span>
              </Button>
              <span className="uppercase tracking-wide text-gray-500">
                Total: {total}
              </span>
              {hasActiveFilters ? (
                <>
                  {appliedFilterSummary.slice(0, 3).map((item, index) => (
                    <span
                      key={`${item}-${index}`}
                      className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200 shadow-sm"
                    >
                      {item}
                    </span>
                  ))}
                  {appliedFilterSummary.length > 3 && (
                    <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 text-xs font-medium border border-dashed border-gray-300">
                      +{appliedFilterSummary.length - 3} oculto(s)
                    </span>
                  )}
                </>
              ) : (
                <span className="uppercase tracking-wide text-gray-500">
                  Nenhum filtro aplicado
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Lista / Tabela */}
        {items.length > 0 || isLoading ? (
          <div className="mt-3 rounded-xl border border-gray-200 shadow-sm overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>Devedor</Table.HeadCell>
                  <Table.HeadCell>Cliente</Table.HeadCell>
                  <Table.HeadCell className="text-center">
                    Usuário
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center">
                    Dt. Inclusão
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center">
                    Status
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center">Ações</Table.HeadCell>
                </Table.Head>

                <Table.Body className="divide-y">
                  {!isLoading
                    ? items.map((it) => (
                        <Table.Row key={it.id} className="bg-white">
                          <Table.Cell className="py-1 font-medium">
                            <div className="flex items-center gap-2">
                              <div>
                                <div>{it.nome || "—"}</div>
                                <div className="text-xs">
                                  {formatDocument(it.documentoDevedor)}
                                </div>
                              </div>
                            </div>
                          </Table.Cell>

                          <Table.Cell>
                            {it?.Company?.name || it?.Company?.socialReason}
                          </Table.Cell>

                          <Table.Cell className="py-1 text-center">
                            {it?.User?.name || "-"}
                          </Table.Cell>

                          <Table.Cell className="py-1 text-center">
                            {it.createdAt
                              ? format(new Date(it.createdAt), "dd/MM/yyyy")
                              : "—"}
                          </Table.Cell>

                          <Table.Cell className="py-1">
                            {isRowLoading(it.id) ? (
                              <div className="flex items-center justify-center py-1">
                                <span className="inline-block h-3 w-3 rounded-full border border-gray-300 border-t-transparent animate-spin" />
                              </div>
                            ) : (
                              <StatusBadge status={it.status} />
                            )}
                          </Table.Cell>

                          <Table.Cell className="py-1 text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <Button
                                size="xs"
                                color="light"
                                onClick={() => {
                                  setDetailId(it.id);
                                  setOpenDetail(true);
                                }}
                                data-tooltip-id="negTableTips"
                                data-tooltip-content="Detalhes"
                                disabled={isRowLoading(it.id)}
                              >
                                <HiOutlineEye className="h-4 w-4" />
                              </Button>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))
                    : Array.from({ length: 10 }).map((_, i) => (
                        <Table.Row key={i}>
                          <Table.Cell colSpan={8}>
                            <div className="h-4 bg-gray-200 animate-pulse rounded" />
                          </Table.Cell>
                        </Table.Row>
                      ))}

                  {!isLoading && items.length === 0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={8}
                        className="text-center text-gray-500 py-8"
                      >
                        Nenhum registro encontrado.
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>

            <Tooltip id="negTableTips" place="top" />
            <Tooltip id="negTableStatus" place="top" />

            {/* Paginação */}
            <div className="flex items-center justify-center mt-4">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(p) => syncToPage(p)}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-gray-200 shadow-sm overflow-hidden bg-white p-8 text-center text-gray-500">
            Nenhum registro encontrado com esses filtros
          </div>
        )}

        {/* Modal Baixa */}
        <Modal
          show={openBaixa}
          size="lg"
          onClose={() => setOpenBaixa(false)}
          popup
        >
          <Modal.Header />
          <Modal.Body>
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">
                Dar baixa na negativação
              </h3>

              <div>
                <Label className="mb-1 block">Motivo *</Label>
                <Select
                  value={motivoCodigo}
                  onChange={(e) => setMotivoCodigo(e.target.value)}
                  required
                >
                  <option value="">Selecione um motivo...</option>
                  {MOTIVOS_BAIXA.map((m) => (
                    <option key={m.Codigo} value={m.Codigo}>
                      {m.Codigo} — {m.Motivo}
                    </option>
                  ))}
                </Select>

                {/* descrição dinâmica */}
                {(() => {
                  const sel = MOTIVOS_BAIXA.find(
                    (m) => m.Codigo === motivoCodigo,
                  );
                  if (!sel) return null;
                  return (
                    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="text-xs text-gray-600">
                        Descrição: {sel.Descricao}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-2">
                <Button color="light" onClick={() => setOpenBaixa(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    const motivoObj = MOTIVOS_BAIXA.find(
                      (m) => m.Codigo === motivoCodigo,
                    );
                    if (!motivoObj) return;
                    const motivoBaixa = motivoObj.Codigo;

                    await baixa.mutateAsync({ id: baixaId, motivoBaixa });
                    setOpenBaixa(false);
                  }}
                  isProcessing={baixa.isLoading}
                  disabled={!motivoCodigo}
                >
                  Confirmar
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Modal Detalhes */}
        <Modal
          show={openDetail}
          size="4xl"
          onClose={() => setOpenDetail(false)}
          className="backdrop-blur-sm"
        >
          <Modal.Header>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-indigo-100">
                <HiShieldCheck className="text-indigo-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">
                  Detalhes da negativação
                </div>
                <div className="text-lg font-semibold">
                  {detail
                    ? `#${detail.id} — ${detail.nome || "—"}`
                    : "Carregando..."}
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            {isDetailLoading && (
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/3" />
                <div className="h-24 bg-gray-100 animate-pulse rounded" />
                <div className="h-40 bg-gray-100 animate-pulse rounded" />
              </div>
            )}

            {!isDetailLoading && isDetailError && (
              <Alert color="failure">
                Não foi possível carregar os detalhes.
              </Alert>
            )}

            {!isDetailLoading && detail && (
              <div>
                {/* Status + Valor + Vencimento */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className=" p-2 rounded-lg border border-gray-200 shadow-sm">
                    <div className="text-xs text-gray-500">Status</div>
                    <div className="mt-1">{detail.status}</div>
                  </div>
                  <di className=" p-2 rounded-lg border border-gray-200 shadow-sm">
                    <div className="text-xs text-gray-500">Valor</div>
                    <div className="mt-1 text-lg font-semibold">
                      {brl(detail.valor)}
                    </div>
                  </di>
                  <div className=" p-2 rounded-lg border border-gray-200 shadow-sm">
                    <div className="text-xs text-gray-500">Vencimento</div>
                    <div className="mt-1 text-lg font-semibold">
                      {detail.dataVencimento
                        ? format(new Date(detail.dataVencimento), "dd/MM/yyyy")
                        : "—"}
                    </div>
                  </div>
                </div>

                {/* Dados principais */}
                <div className="mt-4 mb-1">
                  <div className="text-sm font-semibold">Devedor</div>
                  <div className="text-xs text-gray-500">
                    Documento, contrato e natureza do título
                  </div>
                </div>
                <div className="rounded-lg px-2 py-3 shadow-sm border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Nome</div>
                      <div className="font-medium">{detail.nome || "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Documento</div>
                      <div className="font-mono">
                        {formatDocument(detail.documentoDevedor)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Contrato</div>
                      <div className="font-medium">
                        {detail.contrato || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">
                        Natureza (código)
                      </div>
                      <div className="font-medium">
                        {detail.codigoNatureza || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Criado em</div>
                      <div className="font-medium">
                        {detail.createdAt
                          ? format(
                              new Date(detail.createdAt),
                              "dd/MM/yyyy HH:mm",
                            )
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Atualizado em</div>
                      <div className="font-medium">
                        {detail.updatedAt
                          ? format(
                              new Date(detail.updatedAt),
                              "dd/MM/yyyy HH:mm",
                            )
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Endereço & Contato */}
                <div className="mb-1 mt-4">
                  <div className="text-sm font-semibold">
                    Endereço & Contato
                  </div>
                  <div className="text-xs text-gray-500">
                    Dados de localização e meios de contato
                  </div>
                </div>
                <div className="rounded-lg px-2 py-3 shadow-sm border border-gray-200 mb-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="md:col-span-2">
                      <div className="text-xs text-gray-500">Endereço</div>
                      <div className="font-medium text-sm">
                        {detail.endereco || "—"}
                        {detail.bairro ? `, ${detail.bairro}` : ""}
                        {detail.cidade ? ` - ${detail.cidade}` : ""}
                        {detail.uf ? `/${detail.uf}` : ""}
                        {detail.cep ? ` — CEP ${detail.cep}` : ""}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Contato</div>
                      <div className="font-medium text-sm">
                        {detail.ddd && detail.telefone
                          ? `(${detail.ddd}) ${detail.telefone}`
                          : ""}
                        {detail.email ? ` · ${detail.email}` : ""}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ações rápidas dentro do modal */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-gray-500">
                    ID interno: <span className="font-mono">{detail.id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      color="light"
                      onClick={() => openBaixaModal(detail.id)}
                    >
                      Dar baixa
                    </Button>
                    <Button color="light" onClick={() => setOpenDetail(false)}>
                      Fechar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}
